import type { components } from "@/src/api/api";

/**
 * Un événement, vu par l'administration.
 *
 * ⚠️ **C'est la seule forme de l'API qui porte les coordonnées exactes et
 * l'adresse non masquée d'un événement.** Partout ailleurs — `GET /event/{id}`
 * compris, même appelé par un administrateur — `locationLabel` est masqué dès
 * qu'`isLocationPublic` est faux, et `latitude`/`longitude` n'existent pas.
 * Ce qui s'affiche depuis ce type ne doit donc jamais ressortir dans un
 * composant partagé avec un écran nourri par les routes publiques.
 */
export type AdminEvent = components["schemas"]["AdminEventSchemaOutput"];

/** Une ligne d'effectif : participant validé ou postulant. */
export type EventMember = AdminEvent["participants"][number];

/**
 * Vingt par page.
 *
 * Le serveur plafonne à cent ; vingt tient sur un écran d'admin sans demander
 * de faire défiler la page entière avant d'atteindre la pagination.
 */
export const PAGE_SIZE = 20;

/** Rayon par défaut, appliqué **seulement** quand un centre est donné. */
export const DEFAULT_RADIUS = 50;

/**
 * Les quatre tris du serveur.
 *
 * ⚠️ **`distance` exige un centre de recherche**, sinon le serveur refuse en
 * 400 — et il a raison : trier par une distance qui vaut `null` partout ne
 * range rien. L'écran désactive donc l'onglet tant qu'aucun lieu n'est choisi,
 * et `useEvents` retombe sur `date` si l'URL en porte un quand même.
 */
export const SORTS = ["date", "createdAt", "name", "distance"] as const;

export type EventSort = (typeof SORTS)[number];

export function isEventSort(value: unknown): value is EventSort {
  return SORTS.some((sort) => sort === value);
}

export const ORDERS = ["desc", "asc"] as const;

export type EventOrder = (typeof ORDERS)[number];

export function isEventOrder(value: unknown): value is EventOrder {
  return ORDERS.some((order) => order === value);
}

/**
 * Les statuts filtrables — **les trois du serveur**, `close` compris.
 *
 * ⚠️ **Leurs noms ne veulent pas dire ce qu'ils suggèrent** : `open` accepte les
 * candidatures, `complete` a son effectif au complet mais n'a pas encore été
 * joué, et `close` est **terminé** (état final posé par `setWinners`). Les
 * libellés ne recopient donc pas les codes.
 *
 * `all` n'est pas envoyé au serveur : `status` absent signifie déjà « les
 * trois ».
 */
export const STATUS_FILTERS = ["all", "open", "complete", "close"] as const;

export type EventStatusFilter = (typeof STATUS_FILTERS)[number];

export function isEventStatusFilter(
  value: unknown,
): value is EventStatusFilter {
  return STATUS_FILTERS.some((status) => status === value);
}
